import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {AuthenticatePanelProps} from '@Panels/authenticate-panel/authenticate-panel.types';
import md5 from 'md5';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {MESSAGE_TYPE} from '@Libraries/message-panel.types';
import {authenticateUser, geCurrentUserData, getUserId, isSuperUserLoggedInAsAnotherUser, isUserLoggedIn, logOutUser} from '@Libraries/user.library';
import {AuthContext} from '@Libraries/login-signup-library';
import {getEditPosterURL, isInReviewOrGalleryTemplate} from '@Libraries/poster-editor.library';
import type {PosterObjectBackend} from '@PosterWhiteboard/poster/poster-backend.types';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {User} from '@PosterWhiteboard/user/user.class';
import * as jsondiffpatch from 'jsondiffpatch';
import type {UserObject} from '@PosterWhiteboard/user/user.types';
import {logClientError} from '@Libraries/log.library';
import {isOnline, pollIsUserOnlineTillOnline, stopIsUserOnlinePoll} from '@Libraries/user-online-status.library';
import type {ConflictUserData, SavePosterError} from '@PosterWhiteboard/save-poster/save-poster.types';
import {areCookiesEnabled} from '@Utils/cookie.util';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {Delta} from 'jsondiffpatch';
import debounce from 'lodash/debounce';
import {getUniqueString} from '@Utils/string.util';
import {EditorType} from '@Utils/misc.util';
import type {UserSaveConflictModalProps} from '@Modals/user-save-conflict-modal';
import {shouldShowRepublishButton} from '@Libraries/poster-library';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const SAVED_NOTIFICATION_TIMOUT_DURATION = 5000;
const AUTO_SAVE_INTERVAL = 15000;

interface SavePosterOpts {
  isInTeamSpace?: boolean;
  ignoreConflict?: boolean;
  showLoadingToast?: boolean;
}

interface SaveResponse {
  folder: any;
  poster: SaveResponsePosterData;
}

interface SaveResponsePosterData {
  id?: string;
  hashedID?: string;
  lastModified?: number;
  idLastModifier?: string;
  jsonVersionId?: string | null;
  seoName?: string;
}

interface SaveData {
  ignoreConflict: number;
  isInTeamSpace: number;
  saveSessionId: string;
  posterData: string;
  checksum: string;
  posterId: string;
  posterVersion: number;
  isIncrementalSave: number;
  baseLastModified?: number;
  baseJsonVersionId?: string;
  idFolder?: string;
  debugInfo?: object; // todo umar: temp for logging
  dontCopyTemplateFields?: boolean;
}

export const SAVE_FAILED_DUE_TO_INCOMPLETE_IMAGE_UPLOAD_ERROR_SUBSTRING = 'no hashedFilename or fileExtension found for image with uid:';

export class SavePoster {
  public savedParentPosterBackendObject?: PosterObjectBackend;
  private lastSavedPosterObject?: PosterObjectBackend;
  private isFullPosterSaveRequired = true;
  /**
   * The stored JSON version the editor's state is built on: what the poster loaded as, then what each
   * save produced. Sent with every save so the server refuses to overwrite a save it has not seen.
   * Null while unknown, such as after loading an older version on purpose.
   */
  private baseJsonVersionId: string | null = null;
  private autoSaveInterval: undefined | NodeJS.Timeout;
  private readonly boundOnUnloadWindowSyncToPage;
  private isAutoSaveInitialized = false;
  /**
   * Latched true after the first successful save this session. A signage-published design's render
   * is stale from the moment it is saved, so this keeps the editor's Republish button enabled after
   * a save even though hasUnsavedChanges has reset to false. It stays latched for the session; a
   * republish navigates out of the editor, so the next load recomputes staleness from scratch.
   */
  private hasSavedSinceLoad = false;
  /**
   * Set when the server refused a save as a conflict and the user has yet to pick keep-mine or
   * keep-theirs. Autosave resends nothing while it is set: the refused base is dead, so every retry
   * would be refused the same way, and keep-theirs reloads the page while keep-mine saves explicitly.
   */
  private isAwaitingSaveConflictChoice = false;
  /** Set while a server-side job owns the poster's content; a save would overwrite its result with the blank placeholder. */
  private isSavingPaused = false;
  #isFoldingOpLogBeforeWholeSave = false;
  private readonly saveSessionId: string;
  private readonly debouncedAutosave;
  private readonly poster: Poster;

  constructor(poster: Poster) {
    this.poster = poster;
    this.saveSessionId = getUniqueString();
    this.boundOnUnloadWindowSyncToPage = this.onUnloadWindow.bind(this);
    this.debouncedAutosave = debounce(
      () => {
        this.triggerAutoSave().catch((e: unknown) => {
          console.error(e);
        });
      },
      2000,
      {
        leading: false,
        maxWait: 7000,
      }
    );
    window.addEventListener('beforeunload', this.boundOnUnloadWindowSyncToPage);
  }

  /** Whether this session saves through the op stream (every commit is sent as it is made) instead of posting the poster. */
  public isOpStreamSaveActive(): boolean {
    return this.poster.collaboration.liveEdits.isActive();
  }

  /** The op stream's view of "saved": true while any commit is still unsent or unordered. */
  public onOpStreamSaveStatusChange(hasUnacknowledgedEdits: boolean): void {
    this.poster.redux.updateIsPosterSaving(hasUnacknowledgedEdits);
    if (hasUnacknowledgedEdits) {
      return;
    }
    this.hasSavedSinceLoad = true;
    this.poster.redux.updateDidPosterSaveFailed(false);
    this.updateDidPosterRecentlySave();
    this.poster.posterPreview.triggerDebouncedGeneratePreview();
  }

  /** The server refused the stream, so the poster saves whole again from its current state, right away when autosave is on. */
  public onOpStreamAbandoned(): void {
    this.poster.redux.updateIsPosterSaving(false);
    this.isFullPosterSaveRequired = true;
    void this.#foldOpLogThenSaveWhole();
  }

  /**
   * Edits the log already acknowledged may not be folded yet. Folding them first stamps the stored
   * poster with their sequence, so the fold queued for them cannot later replay them over the
   * newer edits the whole-poster save carries. Saves wait meanwhile; a failed fold is logged and
   * the poster is saved whole all the same.
   */
  async #foldOpLogThenSaveWhole(): Promise<void> {
    this.#isFoldingOpLogBeforeWholeSave = true;
    try {
      await this.poster.collaboration.liveEdits.foldLog();
    } catch (error) {
      window.PMW.log('save-poster: the op log could not be folded before saving the poster whole', error);
    } finally {
      this.#isFoldingOpLogBeforeWholeSave = false;
    }
    this.triggerAutoSaveIfEnabled();
  }

  /** A publish that did not get through; it is retried, and meanwhile the indicator shows the save as failed or offline. */
  public onOpStreamPublishFailed(): void {
    if (!isOnline()) {
      pollIsUserOnlineTillOnline();
      return;
    }
    this.poster.redux.updateDidPosterSaveFailed(true);
  }

  private autoSavePosterOnChange(): void {
    if (this.isOpStreamSaveActive() || !this.isAutoSaveEnabled()) {
      return;
    }

    if (!this.didPosterSavePreviouslyFail()) {
      this.debouncedAutosave();
    }
  }

  public triggerAutoSaveIfEnabled(): void {
    if (this.isAutoSaveEnabled()) {
      void this.triggerAutoSave();
    }
  }

  public isIncrementalSaveAvailable(): boolean {
    return this.isUserIncludedInAutoSaveRollout() && !this.isFullPosterSaveRequired && this.lastSavedPosterObject !== undefined;
  }

  /**
   * This should eventually just return true as we enable autosave for more users
   */
  public isUserIncludedInAutoSaveRollout(): boolean {
    return window.PMW.currentUserOnPageLoad?.isAutosaveAvailable === true;
  }

  public isAutoSaveEnabled(): boolean {
    return (
      this.isUserIncludedInAutoSaveRollout() &&
      !getPosterUi().isEmbeddedMorphModeActive() &&
      !this.isInitalPosterItemsLoadErrorNotificationShowing() &&
      !isInReviewOrGalleryTemplate() &&
      !shouldShowRepublishButton() &&
      !this.poster.isPosterInGeneration() &&
      !this.poster.mode.isWebpage() &&
      !this.poster.mode.isRegen() &&
      !(this.poster.mode.details.type === PosterModeType.EDIT && this.poster.mode.details.posterVersionId) &&
      !this.poster.mode.isGeneration() &&
      isUserLoggedIn() &&
      window.PMW.redux.store.getState().userPreferences.isAutosaveEnabled &&
      !this.poster.isSuperUserEditingOthersPoster() &&
      !isSuperUserLoggedInAsAnotherUser()
    );
  }

  public updateSavedPosterObject(posterBackendObject?: PosterObjectBackend): void {
    this.lastSavedPosterObject = posterBackendObject;
  }

  public setBaseJsonVersionId(jsonVersionId: string | null): void {
    this.baseJsonVersionId = jsonVersionId;
  }

  /**
   * Holds every save (autosave, manual, tab-close) until {@link resumeSaving}, dropping one already
   * debounced. The op stream closes too: its unsent edits belong to the placeholder the server job
   * is about to replace.
   */
  public pauseSaving(): void {
    this.isSavingPaused = true;
    this.debouncedAutosave.cancel();
    this.poster.collaboration.stop();
  }

  /** Saving resumes from the poster as it stands now, so the op stream reopens from its current snapshot. */
  public resumeSaving(): void {
    this.isSavingPaused = false;
    this.poster.collaboration.start();
  }

  public areSavesPaused(): boolean {
    return this.isSavingPaused;
  }

  public setIsFullPosterSaveRequired(val: boolean): void {
    this.isFullPosterSaveRequired = val;
  }

  private getDeltaForSave(posterBackendObject: PosterObjectBackend): Delta {
    return this.getJsonPatchInstanceForSavePoster().diff(this.lastSavedPosterObject, posterBackendObject);
  }

  public getHasSavedSinceLoad(): boolean {
    return this.hasSavedSinceLoad;
  }

  public hasUnsavedChanges(): boolean {
    if (this.isOpStreamSaveActive()) {
      return this.poster.collaboration.liveEdits.hasUnacknowledgedEdits() || this.poster.areAnyImagesUploading();
    }
    if (!this.lastSavedPosterObject) {
      return true;
    }

    if (this.poster.areAnyImagesUploading()) {
      return true;
    }

    try {
      return this.getJsonPatchInstanceForSavePoster().diff(this.lastSavedPosterObject, this.poster.getBackendObject()) !== undefined;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public onDispose(): void {
    this.disableTabClosePrompt();
    clearInterval(this.autoSaveInterval);
    stopIsUserOnlinePoll();
  }

  private disableTabClosePrompt(): void {
    window.removeEventListener('beforeunload', this.boundOnUnloadWindowSyncToPage);
  }

  public async save(opts: SavePosterOpts = {}, showLoadingToast = true, onCloseAuthenticateModal?: () => void): Promise<void> {
    if (this.isOpStreamSaveActive()) {
      await this.saveThroughOpStream(showLoadingToast);
      return;
    }
    if (this.isAutoSaveEnabled()) {
      try {
        await this.triggerAutoSave(opts);
      } catch (e) {
        console.error(e);
      }
      return;
    }

    await this.triggerManualSave(opts, showLoadingToast, onCloseAuthenticateModal);
  }

  public async triggerManualSave(
    {isInTeamSpace = false, ignoreConflict = false}: SavePosterOpts = {},
    showLoadingToast = true,
    onCloseAuthenticateModal?: () => void
  ): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    const trace = new Error().stack;
    await authenticateUser(
      async (): Promise<void> => {
        try {
          if (showLoadingToast) {
            showLoading('savingPoster', {
              text: window.i18next.t('pmwjs_saving'),
            });
          }

          await this.doSavePoster({
            isInTeamSpace,
            ignoreConflict,
            showLoadingToast,
          });
        } catch (e) {
          if (this.isSaveErrorDueToIncompleteImageUploads(e)) {
            void logClientError(e, {
              saveTrace: trace,
              posterReduxData: {...window.PMW.redux.store.getState().posterEditor},
            });
          }
          void this.onManualSaveError(e as SavePosterError | undefined, showLoadingToast);
        } finally {
          if (showLoadingToast) {
            hideLoading('savingPoster');
          }
        }
      },
      onCloseAuthenticateModal,
      this.getAuthenticateModalProps()
    );
  }

  /**
   * The dialog shows the gallery template the user is customizing (D47): the template's own preview, captured
   * at boot, so an in-session template swap keeps the original artwork and a blank canvas gets the general art.
   */
  private getAuthenticateModalProps(): Partial<AuthenticatePanelProps> {
    const {sourcePosterPreviewUrl} = this.poster;
    if (this.poster.getGalleryTemplateId() === undefined || sourcePosterPreviewUrl === null) {
      return {authContext: AuthContext.SAVE_DESIGN};
    }
    return {authContext: AuthContext.SAVE_DESIGN, featureImageUrl: sourcePosterPreviewUrl};
  }

  public isPosterSaving(): boolean {
    return window.PMW.redux.store.getState().posterEditor.isPosterSaving;
  }

  public isInitalPosterItemsLoadErrorNotificationShowing(): boolean {
    return window.PMW.redux.store.getState().initalPosterItemsLoadError.show;
  }

  public didPosterSavePreviouslyFail(): boolean {
    return window.PMW.redux.store.getState().posterEditor.didPosterSaveFailed;
  }

  public hasInProgressChanges(): boolean {
    return Object.keys(this.poster.inProgressChanges).length !== 0;
  }

  private canSave(): boolean {
    return (
      !this.isSavingPaused &&
      !this.#isFoldingOpLogBeforeWholeSave &&
      this.hasUnsavedChanges() &&
      !this.poster.areAnyImagesUploading() &&
      !this.isPosterSaving() &&
      !this.isInitalPosterItemsLoadErrorNotificationShowing() &&
      !this.hasInProgressChanges() &&
      !this.poster.areItemsModifying() &&
      !window.PMW.redux.store.getState().posterEditor.isVersionHistoryModeActive
    );
  }

  private isGalleryTemplateDataUpdatedInPages(posterData: unknown): boolean {
    if (typeof posterData !== 'object' || posterData === null || !('pages' in posterData)) {
      return false;
    }
    const pages = (posterData as {pages?: {pagesHashMap?: Record<string, unknown>}}).pages?.pagesHashMap;
    return pages ? Object.values(pages).some((p) => typeof p === 'object' && p !== null && (p as {galleryTemplateData?: unknown}).galleryTemplateData != null) : false;
  }

  /**
   * The Save button while saving by op stream: every commit is already on its way, so saving means
   * waiting for the server to acknowledge the last of them and fold them into the stored poster.
   */
  private async saveThroughOpStream(showLoadingToast: boolean): Promise<void> {
    if (this.poster.areAnyImagesUploading() || this.isInitalPosterItemsLoadErrorNotificationShowing()) {
      return;
    }
    if (showLoadingToast) {
      showLoading('savingPoster', {
        text: window.i18next.t('pmwjs_saving'),
      });
    }
    try {
      await this.poster.collaboration.liveEdits.flush();
      this.hasSavedSinceLoad = true;
      this.poster.posterPreview.triggerDebouncedGeneratePreview();
      this.onPosterSaved(showLoadingToast);
      this.updateDidPosterRecentlySave();
    } catch (e) {
      if (!this.isOpStreamSaveActive()) {
        // The server refused the stream while this save waited on it; the poster saves whole instead.
        await this.save({}, showLoadingToast);
        return;
      }
      void this.onManualSaveError(e as SavePosterError | undefined, showLoadingToast);
    } finally {
      if (showLoadingToast) {
        hideLoading('savingPoster');
      }
    }
  }

  private async triggerAutoSave({isInTeamSpace = false, ignoreConflict = false}: SavePosterOpts = {}): Promise<void> {
    if (this.isOpStreamSaveActive() || !this.canSave() || !this.isAutoSaveEnabled()) {
      return;
    }
    if (this.isAwaitingSaveConflictChoice && !ignoreConflict) {
      return;
    }

    try {
      await this.doSavePoster({
        isInTeamSpace,
        ignoreConflict,
        showLoadingToast: false,
      });
      this.isAwaitingSaveConflictChoice = false;
      this.poster.redux.updateDidPosterSaveFailed(false);
    } catch (e) {
      void this.handleSaveErrorForAutoSave(e);
      throw e;
    }
  }

  private async doSavePoster({isInTeamSpace = false, ignoreConflict = false, showLoadingToast = true}: SavePosterOpts = {}): Promise<void> {
    if (this.isPosterSaving()) {
      return;
    }

    this.poster.redux.updateIsPosterSaving(true);
    try {
      // A save right after load forces any still-deferred page content to finish loading so the
      // pre-save passes below (menu wrapping info, invisible-item cleanup) see every item.
      await this.poster.pages.waitForDeferredPagesContent();
      this.poster.deleteItemsNotVisibleOnPoster(false);
      // We're guaranteed a logged-in user. Use their info.
      this.poster.idLastModifier = getUserId()?.toString()!;
      // todo umar: temp for logging
      let creatorInfoFlow = 'normal';
      const originalUserIdInCookie = window.PMW.getUserId();
      let userDataFromServer: Partial<UserObject> | undefined;

      if (this.poster.creator === undefined) {
        creatorInfoFlow = 'undefined';
        const userData = (await geCurrentUserData()) as Partial<UserObject>;
        this.poster.creator = new User(userData);
      } else if (this.poster.creator.id === '-1') {
        creatorInfoFlow = 'defaultId';
        userDataFromServer = (await geCurrentUserData()) as Partial<UserObject> | undefined;
        this.poster.creator.copyVals((await geCurrentUserData()) as Partial<UserObject>);
      }
      this.poster.setMenuItemsWrappingInfo();
      const posterObjectToSave = this.poster.getBackendObject();
      const snapshotAtSave = this.poster.history.getCurrentSnapshot();
      const isIncrementalSave = this.isIncrementalSaveAvailable();
      const posterData = isIncrementalSave ? this.getDeltaForSave(posterObjectToSave) : posterObjectToSave;
      if (posterData === undefined || posterObjectToSave.pages.length === 0) {
        this.poster.redux.updateIsPosterSaving(false);
        return;
      }

      const posterDataJson = JSON.stringify({
        poster: posterData,
      });
      const data: SaveData = {
        saveSessionId: this.saveSessionId,
        ignoreConflict: ignoreConflict ? 1 : 0,
        isInTeamSpace: isInTeamSpace ? 1 : 0,
        checksum: md5(posterDataJson),
        posterData: posterDataJson,
        posterId: this.poster.id,
        isIncrementalSave: isIncrementalSave ? 1 : 0,
        posterVersion: POSTER_VERSION.CURRENT,
        dontCopyTemplateFields: this.poster.isTemplateApplied || this.isGalleryTemplateDataUpdatedInPages(posterData),
      };

      if (isIncrementalSave) {
        data.baseLastModified = this.lastSavedPosterObject?.lastModified;
      }
      if (this.baseJsonVersionId !== null) {
        data.baseJsonVersionId = this.baseJsonVersionId;
      }
      if (this.poster.folder) {
        data.idFolder = this.poster.folder.id;
      }

      if (this.poster.creator.id === '-1') {
        // todo umar: temp for logging
        data.debugInfo = {
          creatorInfoFlow,
          isEmbedded: window.PMW.isEmbedded() ? 1 : 0,
          isPWA: window.PMW.isPWA() ? 1 : 0,
          userDataFromServer: {
            id: userDataFromServer?.id,
            isNull: userDataFromServer === undefined ? 1 : 0,
          },
          cookieUserId: window.PMW.getUserId(),
          originalUserIdInCookie,
          areCookiesEnabled: areCookiesEnabled(),
        };
      }

      const isFirstSaveOfPoster = !this.poster.id;
      const response = (await window.PMW.writeLocal('posterbuilder/savePoster', data)) as SaveResponse;
      const {jsonVersionId, ...savedPosterData} = response.poster;
      if (jsonVersionId !== undefined) {
        this.baseJsonVersionId = jsonVersionId;
      }
      await this.poster.updateFromObject(savedPosterData, {updateRedux: false, fireModifiedEvent: false, undoable: false});
      this.lastSavedPosterObject = {
        ...posterObjectToSave,
        ...savedPosterData,
      };
      this.isFullPosterSaveRequired = false;
      this.hasSavedSinceLoad = true;
      this.poster.redux.updateReduxData();
      this.updateUrl();
      this.poster.updateTitle();
      if (isFirstSaveOfPoster) {
        this.poster.posterPreview.triggerGeneratePreview();
        // The poster now has an id and a hash, so from here on its edits can stream.
        this.poster.collaboration.startAfterFirstSave(snapshotAtSave);
      } else {
        this.poster.posterPreview.triggerDebouncedGeneratePreview();
      }
      this.onPosterSaved(showLoadingToast);
      this.updateDidPosterRecentlySave();
    } finally {
      this.poster.redux.updateIsPosterSaving(false);
      this.poster.isTemplateApplied = false;
    }
  }

  private updateDidPosterRecentlySave(): void {
    this.poster.redux.updateDidPosterRecentlySave(true);
    setTimeout((): void => {
      this.poster.redux.updateDidPosterRecentlySave(false);
    }, 1500);
  }

  private async handleSaveErrorForAutoSave(e: unknown): Promise<void> {
    if ((e as SavePosterError)?.code === 'json-diff-patch-error') {
      this.setIsFullPosterSaveRequired(true);
      await this.save();
      return;
    }

    if (!this.didPosterSavePreviouslyFail() && (await this.showSpecificPosterSaveError(e as SavePosterError | undefined))) {
      this.poster.redux.updateDidPosterSaveFailed(true);
      return;
    }

    // We change the autosave icon to offline in this case so no need to show an error
    if (!isOnline()) {
      pollIsUserOnlineTillOnline();
      return;
    }

    if (!this.didPosterSavePreviouslyFail()) {
      this.poster.redux.updateDidPosterSaveFailed(true);
      getPosterUi().openMessageModal({
        type: MESSAGE_TYPE.DANGER,
        text: window.i18next.t('pmwjs_save_poster_error'),
      });
    }
  }

  private async showSpecificPosterSaveError(error?: SavePosterError): Promise<boolean> {
    if (!error) {
      return false;
    }

    switch (error.code) {
      case 'cannot-edit-poster': {
        if (error.data.isUserContributor) {
          getPosterUi().openMessageModal({
            title: window.i18next.t('pmwjs_permission_denied'),
            text: window.i18next.t('pmwjs_edit_permission_denied'),
          });
        }

        getPosterUi().openMessageModal({
          title: window.i18next.t('pmwjs_design_belongs_to_another_user_title'),
          text: window.i18next.t('pmwjs_design_belongs_to_another_user'),
        });
        return true;
      }
      case 'cannot-copy-poster':
        getPosterUi().openMessageModal({
          title: window.i18next.t('pmwjs_permission_denied'),
          text: window.i18next.t('pmwjs_copy_permission_denied'),
        });
        return true;

      case 'not-logged-in':
        await this.handleLoggedOutUser(window.i18next.t('pmwjs_logged_out_title'), window.i18next.t('pmwjs_logged_out_message'));
        return true;

      case 'save-conflict': {
        if (error.data.conflictUser) {
          this.onSaveConflict(error.data.conflictUser, error.data.userProfileImage);
          return true;
        }
        break;
      }

      case 'rejected-template': {
        getPosterUi().openMessageModal({
          title: error.data.rejectionTitle,
          text: error.data.rejectionText,
        });
        return true;
      }

      default:
        break;
    }
    return false;
  }

  private onPosterSaved(doShowMessageGrowl = true): void {
    if (this.poster.mode.details.type === PosterModeType.REGEN) {
      window.location.href = window.PMW.util.site_url(`cart/requestDownloadRegen/${this.poster.mode.details.orderId}`);
      return;
    }
    if (doShowMessageGrowl) {
      getPosterUi().showMessageGrowl({
        text: window.i18next.t('pmwjs_saved_successfully'),
        key: 'savedPoster',
        interval: SAVED_NOTIFICATION_TIMOUT_DURATION,
      });
    }
    void geCurrentUserData().then((userData: Partial<UserObject> | undefined) => {
      if (userData?.verificationNeededStatus) {
        window.location.href = window.PMW.util.site_url('authenticate/verificationneeded?saved=1');
      } else {
        void window.PMW.premiumDialogs.openTrialDialog(true);
      }
    });

    this.poster.resizePoster.copyVals(this.poster.toObject());
  }

  private onUnloadWindow(e: BeforeUnloadEvent): string | undefined {
    if (this.alertBeforeClosing()) {
      e.preventDefault();
      e.returnValue = null;
      this.poster.collaboration.liveEdits.sendBufferedOpsBeforeUnload();
      this.triggerAutoSaveIfEnabled();
      return window.i18next.t('pmwjs_onUnloadWindow');
    }
    return undefined;
  }

  public alertBeforeClosing(): boolean {
    if (this.isSavingPaused || this.poster.mode.isGeneration() || this.poster.mode.isWebpage() || this.isInitalPosterItemsLoadErrorNotificationShowing()) {
      return false;
    }

    if (this.poster.mode.isCopy() && !this.poster.id) {
      return this.didUserChangeTemplate();
    }

    return this.hasUnsavedChanges();
  }

  public consoleUnsavedChanges(): void {
    console.log(this.getJsonPatchInstanceForSavePoster().diff(this.lastSavedPosterObject, this.poster.getBackendObject()));
  }

  public consoleChangeTemplate(): void {
    try {
      const jsonPatchInstance = jsondiffpatch.create({
        propertyFilter: (name: string, context: jsondiffpatch.DiffContext) => {
          if (context.childName === undefined) {
            return (
              name === 'height' ||
              name === 'pages' ||
              name === 'width' ||
              name === 'audioClips' ||
              name === 'type' ||
              name === 'units' ||
              name === 'userHeight' ||
              name === 'userWidth'
            );
          }

          return true;
        },
      });
      console.log(jsonPatchInstance.diff(this.savedParentPosterBackendObject, this.poster.getBackendObject()));
    } catch (e) {
      console.error(e);
    }
  }

  private didUserChangeTemplate(): boolean {
    try {
      const jsonPatchInstance = jsondiffpatch.create({
        propertyFilter: (name: string, context: jsondiffpatch.DiffContext) => {
          if (context.childName === undefined) {
            return (
              name === 'height' ||
              name === 'pages' ||
              name === 'width' ||
              name === 'audioClips' ||
              name === 'type' ||
              name === 'units' ||
              name === 'userHeight' ||
              name === 'userWidth'
            );
          }

          return !(
            typeof context.left === 'object' &&
            context.left &&
            'gitype' in context.left &&
            context.left.gitype === 'video' &&
            (name === 'frameRate' || name === 'duration')
          );
        },
      });
      return jsonPatchInstance.diff(this.savedParentPosterBackendObject, this.poster.getBackendObject()) !== undefined;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  private async onManualSaveError(saveErrorData?: SavePosterError, showLoadingToast = true): Promise<void> {
    if (saveErrorData?.code === 'json-diff-patch-error') {
      this.setIsFullPosterSaveRequired(true);
      await this.save({}, showLoadingToast);
      return;
    }

    if (!(await this.showSpecificPosterSaveError(saveErrorData))) {
      console.error(saveErrorData);
      getPosterUi().openMessageModal({
        type: MESSAGE_TYPE.DANGER,
        text: window.i18next.t('pmwjs_save_poster_error'),
      });
    }
  }

  /**
   * After a poster is saved, makes sure the URL is updated to include the poster's hashed ID and uses the /load
   * server-side method used for editing the poster. This allows the user to copy the URL to edit their poster in the
   * future.
   */
  private updateUrl(): void {
    if (window.history && window.location && window.location.pathname && window.location.pathname.length > 0) {
      // The regex expression removes any leading slash in the page path
      const path = window.PMW.util.getCurrentPagePath().replace(/^\/(.*)/, '$1');
      const pathSegments: string[] = path.split('/');
      const separator = window.PMW.util.getPagePathSeparatorToken();
      const domain = window.location.pathname.split(separator)[0];
      let c = '';
      let m = '';
      const newPath = [];

      newPath.push(domain + separator);

      if (pathSegments !== undefined && pathSegments[0]) {
        [c] = pathSegments;
      }
      if (pathSegments !== undefined && pathSegments[1]) {
        [, m] = pathSegments;
      }

      // don't update the URL if it was already the load URL
      if (c.length > 0 && m !== 'load') {
        newPath.push(c);
        newPath.push('load');
        newPath.push(this.poster.hashedID);

        let loadUrl = newPath.join('/');

        if (window.PMW.isEmbedded()) {
          const embeddedParams = window.PMW.util.getEmbeddedParamsFromUrlAsArray();
          if (embeddedParams.length > 0) {
            loadUrl += `?${embeddedParams.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&')}`;
          }
        }

        window.history.replaceState('saved', 'saved', loadUrl);
        this.poster.history.reset();
      }
    }
  }

  /**
   * Displays a message informing the user that they are not logged in and must log in before they can save.
   * When this dialog is closed, the authentication dialog is opened.
   */
  private async handleLoggedOutUser(title: string, text: string): Promise<void> {
    try {
      await logOutUser();
    } catch (e) {
      console.error('error in logging user out. Details: ');
      console.error(e);
    }
    return new Promise((resolve) => {
      getPosterUi().openMessageModal({
        title,
        text,
        onClose: authenticateUser.bind(null, async () => {
          await this.save();
          resolve();
        }),
      });
    });
  }

  private getJsonPatchInstanceForSavePoster(): jsondiffpatch.DiffPatcher {
    return jsondiffpatch.create({
      propertyFilter: (name: string, context: jsondiffpatch.DiffContext) => {
        if (typeof context.left === 'object' && context.left && 'gitype' in context.left && context.left.gitype === 'video' && (name === 'frameRate' || name === 'duration')) {
          return false;
        }

        return true;
      },
    });
  }

  private onSaveConflict(user: ConflictUserData, userProfileImage: string): void {
    trackPosterBuilderGA4Events(GA4EventName.SAVE_CONFLICT);
    this.isAwaitingSaveConflictChoice = true;
    const props: UserSaveConflictModalProps = {
      currentUserPictureURL: userProfileImage,
      otherUserName: user.name,
      otherUserPictureURL: user.profilePictureURLSmall,
      editorType: EditorType.DESIGN,
      onAcceptYours: () => {
        trackPosterBuilderGA4Events(GA4EventName.SAVE_CONFLICT_SAVE_YOURS);
        void this.save({
          ignoreConflict: true,
        });
      },
      onAcceptTheirs: () => {
        trackPosterBuilderGA4Events(GA4EventName.SAVE_CONFLICT_SAVE_THEIRS);
        this.disableTabClosePrompt();
        window.location.href = getEditPosterURL(this.poster.hashedID);
      },
      onDismiss: () => {
        this.resumeAutoSaveAfterDismissedConflict();
      },
    };
    if (isEditorMobileVariant()) {
      getPosterUi().openUserSaveConflictModal(props);
    } else {
      this.poster.redux.updateSaveConflictDataDetails({
        ...props,
        triggerOpenSaveConflictPopup: true,
      });
    }
  }

  /**
   * A dismissed conflict dialog must not leave the design silently unsaved for the rest of the
   * session, so autosave resumes; its next attempt is refused again and re-raises the dialog, which
   * is why the failed flag is cleared too.
   */
  private resumeAutoSaveAfterDismissedConflict(): void {
    this.isAwaitingSaveConflictChoice = false;
    this.poster.redux.updateDidPosterSaveFailed(false);
  }

  private isSaveErrorDueToIncompleteImageUploads(e: unknown): boolean {
    return !!(typeof e === 'object' && e && 'message' in e && typeof e.message === 'string' && e.message.includes(SAVE_FAILED_DUE_TO_INCOMPLETE_IMAGE_UPLOAD_ERROR_SUBSTRING));
  }

  public initAutoSave(): void {
    if (this.isAutoSaveInitialized) {
      return;
    }

    this.isAutoSaveInitialized = true;
    this.poster.on('modified', this.autoSavePosterOnChange.bind(this));
    this.autoSaveInterval = setInterval(() => {
      this.debouncedAutosave();
    }, AUTO_SAVE_INTERVAL);
    this.debouncedAutosave();
  }
}
